// Google Drive adapter — fulfils CloudStorageProvider via Google Identity
// Services (OAuth) + Drive v3 (files/about endpoints).
//
// Scope: `drive.file` → we can only see/modify files we created. Every file
// lives under the top-level "drawOla" folder and is tagged with
// `appProperties.drawOlaApp = "1"`.

import { unpackEnvelope } from "../../envelope";

import type {
  CloudFileMeta,
  CloudStorageProvider,
  DrawolaEnvelope,
} from "../../types";

import { createDriveAuth } from "./auth";
import { MAX_UPLOAD_BYTES, MIME_TYPE } from "./constants";
import { createDriveClient } from "./driveApi";

import type { DriveFile } from "./driveApi";

function toMeta(f: DriveFile): CloudFileMeta {
  return {
    providerFileId: f.id,
    name: f.name,
    modifiedTime: Date.parse(f.modifiedTime),
    version: f.version,
    sizeBytes: f.size ? Number(f.size) : undefined,
  };
}

/** Creates the Google Drive cloud storage provider. */
export function createGoogleDriveProvider(
  clientId: string,
  authUrl: string,
): CloudStorageProvider {
  const auth = createDriveAuth(clientId, authUrl);
  const client = createDriveClient(auth);

  return {
    id: "googleDrive",
    displayName: "Google Drive",
    mimeType: MIME_TYPE,
    maxUploadBytes: MAX_UPLOAD_BYTES,

    init: () => auth.init(),
    authenticate: (opts) => auth.authenticate(opts),
    signOut: () => auth.signOut(),
    getAuthState: () => auth.getAuthState(),
    subscribeAuth: (l) => auth.subscribeAuth(l),

    async listFiles() {
      const files = await client.list();
      return files.map(toMeta);
    },

    /** Metadata only, no content download. */
    async statFile(id) {
      return toMeta(await client.getMetadata(id));
    },

    async readFile(id) {
      const [meta, raw] = await Promise.all([
        client.getMetadata(id),
        client.getContent(id),
      ]);
      const envelope = unpackEnvelope(raw);
      return { meta: toMeta(meta), envelope };
    },

    async writeFile(args: {
      providerFileId?: string;
      name: string;
      envelope: DrawolaEnvelope;
      ifMatchVersion?: string;
    }) {
      if (args.providerFileId) {
        const updated = await client.update({
          id: args.providerFileId,
          name: args.name,
          envelope: args.envelope,
          ifMatchVersion: args.ifMatchVersion,
        });
        return toMeta(updated);
      }
      const created = await client.create({
        name: args.name,
        envelope: args.envelope,
      });
      return toMeta(created);
    },

    async deleteFile(id) {
      await client.remove(id);
    },

    async renameFile(id, newName) {
      const updated = await client.rename(id, newName);
      return toMeta(updated);
    },
  };
}
